import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Info } from "lucide-react";
import { api } from "@/lib/api";
import HealthStatus from "@/components/HealthStatus";
import { OrganIcon, SectionTitle } from "@/components/Shared";
import { CENSUS_TO_UI, CENS_STATES, SYNC_PENDING } from "@/components/CensimentoFigure";
import CensusBasisPanel from "@/components/CensusBasisPanel";

export default function HealthPage() {
  const [health, setHealth] = useState(null);
  const [organs, setOrgans] = useState([]);
  const [basisOrganId, setBasisOrganId] = useState(null);

  useEffect(() => {
    api.get("/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/organs").then((r) => setOrgans(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4" data-testid="health-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4"><ChevronLeft size={14} /> Organismo</Link>
      <div className="mb-5 fade-up">
        <p className="text-xs tracking-[0.25em] text-gray-500">SALUTE · QUADRO GENERALE</p>
        <h1 className="font-display text-3xl font-bold mt-1">Come sta la mia azienda?</h1>
      </div>

      {/* H1 interim (GO 15.09.2026): widget aggregato = Business Health legacy, marcato
          "in evoluzione" fino a PEND-HOME-01. NON deriva dal Census (CENSUS QUALITY ≠ BUSINESS HEALTH). */}
      <div className="mb-6 relative">
        <HealthStatus health={health} large />
        <span className="absolute top-3 right-4 text-[9px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full text-gray-500"
          style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(11,17,32,0.7)" }}
          data-testid="health-widget-evolution">in evoluzione</span>
      </div>

      <div className="glass rounded-3xl p-5 mb-6 fade-up">
        <SectionTitle>Regole di valutazione</SectionTitle>
        <ul className="text-sm text-gray-400 space-y-2 leading-relaxed">
          <li>· Lo stato <span className="text-emerald-400">verde</span> richiede dati sufficienti e aggiornati: mai assegnato con informazioni incomplete.</li>
          <li>· Con dati insufficienti lo stato è <span className="text-gray-300">grigio</span>, mai verde.</li>
          <li>· Copertura dati e stato di salute sono sempre mostrati separatamente.</li>
          <li>· Le stime non sono mai presentate come fatti confermati.</li>
        </ul>
      </div>

      {/* H3 interim (GO §1.A/§3): queste righe mostrano COMPLETEZZA DATI / CENSIMENTO, non salute */}
      <SectionTitle>Stato censimento per organo</SectionTitle>
      <p className="text-[11px] text-gray-500 mb-3 -mt-2" data-testid="health-census-note">
        Quanto DAD conosce e verifica di ogni area — non la salute del business.
      </p>
      <div className="space-y-2" data-testid="health-organs-list">
        {organs.map((o) => {
          // DG-PC-04 (GO 15.09.2026): display source = proiezione census, MAI legacy status/coverage
          const st = CENS_STATES[CENSUS_TO_UI[o.census_state]] || SYNC_PENDING;
          const cov = o.census_coverage ?? null;  // UNKNOWN ≠ 0%
          return (
            <Link key={o.organ_id} to={o.route} data-testid={`health-organ-${o.organ_id}`}
              className="glass rounded-2xl px-4 py-3 flex items-center gap-3 hover:border-cyan-400/30 transition-colors">
              <OrganIcon icon={o.icon} size={16} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-200 truncate">{o.business_name || o.business_function} · <span className="text-gray-500">{o.anatomical_name}</span></div>
              </div>
              <span className="text-[11px] text-gray-500 shrink-0 hidden sm:block" data-testid={`health-organ-coverage-${o.organ_id}`}>
                {cov == null ? "—" : `${Math.round(cov)}%`}
                {o.census_qualified ? <span className="text-amber-400" data-testid={`health-organ-qualified-${o.organ_id}`}> · qualificata</span> : ""}
              </span>
              <span className="text-[11px] font-medium shrink-0" style={{ color: st.color }}
                data-testid={`health-organ-state-${o.organ_id}`}>{st.label}</span>
              <span role="button" tabIndex={0} aria-label="Perché questa percentuale?"
                data-testid={`health-basis-open-${o.organ_id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBasisOrganId(o.organ_id); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); setBasisOrganId(o.organ_id); } }}
                className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-gray-500 hover:text-cyan-300 transition-colors cursor-pointer"
                style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)" }}>
                <Info size={10} />
              </span>
            </Link>
          );
        })}
      </div>

      {basisOrganId && (
        <CensusBasisPanel organId={basisOrganId}
          organName={organs.find((x) => x.organ_id === basisOrganId)?.business_name}
          onClose={() => setBasisOrganId(null)} />
      )}
    </div>
  );
}
